import { WifiOff, RefreshCw, Home, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import CosmicBackground from "@/components/CosmicBackground";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

interface OfflineFallbackProps {
  status?: string;
}

export const OfflineFallback = ({ status = "unknown" }: OfflineFallbackProps) => {
  const navigate = useNavigate();
  const [isClearing, setIsClearing] = useState(false);

  const handleRetry = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate("/");
  };

  const handleClearCache = async () => {
    setIsClearing(true);
    try {
      // Unregister all service workers
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
      }
      // Clear all caches
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
      }
      // Reload after clearing
      window.location.reload();
    } catch (error) {
      console.error("Failed to clear cache:", error);
      setIsClearing(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* Reuse the cosmic background */}
      <CosmicBackground />

      {/* Dark overlay for readability */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-10" />

      {/* Content */}
      <div className="relative z-20 min-h-screen flex items-center justify-center p-4 animate-fade-in">
        {/* Glassmorphism card */}
        <div 
          className="max-w-md w-full bg-white/5 border border-white/10 rounded-2xl p-8 text-center"
          style={{
            boxShadow: '0 0 40px rgba(0, 194, 255, 0.15), 0 0 60px rgba(112, 0, 255, 0.1)'
          }}
        >
          {/* Debug info - VISIBLE for diagnostics */}
          <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500/40 rounded-lg text-left text-xs font-mono">
            <p className="text-yellow-300 font-bold mb-1">🔧 REACT Component (not offline.html)</p>
            <p className="text-yellow-200">Hook Status: <span className="text-white">{status}</span></p>
            <p className="text-yellow-200">navigator.onLine: <span className="text-white">{String(typeof navigator !== 'undefined' ? navigator.onLine : 'N/A')}</span></p>
            <p className="text-yellow-200">Time: <span className="text-white">{new Date().toISOString()}</span></p>
            <p className="text-yellow-200">URL: <span className="text-white">{typeof window !== 'undefined' ? window.location.href : 'N/A'}</span></p>
          </div>

          {/* Pulsing icon */}
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-full bg-brand-cyan/10 animate-offline-pulse">
              <WifiOff className="w-12 h-12 text-brand-cyan" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-display font-bold text-foreground mb-3">
            You're Offline
          </h1>

          {/* Subtitle */}
          <p className="text-muted-foreground mb-2">
            ThePromptSpace needs an internet connection to sync prompts and creators.
          </p>

          {/* Reassuring line */}
          <p className="text-sm text-muted-foreground/70 mb-8">
            Your work isn't lost. We'll reconnect automatically when you're back online.
          </p>

          {/* Actions */}
          <div className="space-y-3">
            {/* Primary CTA */}
            <Button
              onClick={handleRetry}
              className="w-full bg-gradient-to-r from-brand-cyan via-brand-purple to-brand-pink text-white font-semibold hover:opacity-90 transition-opacity"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry Connection
            </Button>

            {/* Secondary CTA */}
            <Button
              variant="outline"
              onClick={handleGoHome}
              className="w-full border-white/20 bg-white/5 hover:bg-white/10 text-foreground"
            >
              <Home className="w-4 h-4 mr-2" />
              Go to Home
            </Button>

            {/* Cache clear button */}
            <Button
              variant="outline"
              onClick={handleClearCache}
              disabled={isClearing}
              className="w-full border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {isClearing ? "Clearing..." : "Reset PWA Cache"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
